import { Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatAutocomplete } from '@angular/material/autocomplete';
import {debounceTime, filter, forkJoin, map, Subject, takeUntil} from 'rxjs';
import { fuseAnimations } from '@fuse/animations/public-api';
import {IcDataService} from "../../../services/ic-data.service";
import {Router} from "@angular/router";
import {IcDataStudioScriptsService} from "../../../services/ic-data.studio-scripts.service";

@Component({
    selector     : 'search',
    templateUrl  : './search.component.html',
    styleUrls: ['./search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    exportAs     : 'fuseSearch',
    animations   : fuseAnimations
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy
{
    @Input() appearance: 'basic' | 'bar' = 'basic';
    @Input() debounce = 300;
    @Input() minLength = 2;
    @Output() search: EventEmitter<any> = new EventEmitter<any>();

    opened = false;
    resultSets: any[];
    searchControl: UntypedFormControl = new UntypedFormControl();
    private _matAutocomplete: MatAutocomplete;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _router: Router,
        private _ICDataService: IcDataService,
        private _ICScriptService: IcDataStudioScriptsService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any
    {
        return {
            'search-appearance-bar'  : this.appearance === 'bar',
            'search-appearance-basic': this.appearance === 'basic',
            'search-opened'          : this.opened
        };
    }

    /**
     * Setter for bar search input
     *
     * @param value
     */
    @ViewChild('barSearchInput')
    set barSearchInput(value: ElementRef)
    {
        // If the value exists, it means that the search input
        // is now in the DOM, and we can focus on the input..
        if ( value )
        {
            // Give Angular time to complete the change detection cycle
            setTimeout(() => {

                // Focus to the input element
                value.nativeElement.focus();
            });
        }
    }

    /**
     * Setter for mat-autocomplete element reference
     *
     * @param value
     */
    @ViewChild('matAutocomplete')
    set matAutocomplete(value: MatAutocomplete)
    {
        this._matAutocomplete = value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void
    {
        // Appearance
        if ( 'appearance' in changes )
        {
            // To prevent any issues, close the
            // search after changing the appearance
            this.close();
        }
    }

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to the search field value changes
        this.searchControl.valueChanges
            .pipe(
                debounceTime(this.debounce),
                takeUntil(this._unsubscribeAll),
                map((value) => {

                    // Set the resultSets to null if there is no value or
                    // the length of the value is smaller than the minLength
                    // so the autocomplete panel can be closed
                    if ( !value || value.length < this.minLength )
                    {
                        this.resultSets = null;
                    }

                    // Continue
                    return value;
                }),
                // Filter out undefined/null/false statements and also
                // filter out the values that are smaller than minLength
                filter(value => value && value.length >= this.minLength)
            )
            .subscribe((value) => {
                forkJoin({
                    agents: this._ICDataService.getAgentList(),
                    skills: this._ICDataService.getSkills(),
                    pocs: this._ICDataService.getPointsOfContact(),
                    scripts: this._ICScriptService.getStudioScripts(),
                    dispositions: this._ICDataService.getDispositions(),
                })
                .subscribe(
                    {
                        next: (res)=>{
                            const filteredSkills = res.skills.filter(item => {
                                    return  item.skillId.toString().toLowerCase().includes(value.toLowerCase()) ||
                                        item?.notes?.toLowerCase().includes(value.toLowerCase()) ||
                                        item.skillName.toLowerCase().includes(value.toLowerCase())
                                }
                            ).slice(0, 10);
                            const filteredAgents = res.agents.filter(item => {
                                    return  item.agentId.toString().toLowerCase().includes(value.toLowerCase()) ||
                                        item?.emailAddress?.toLowerCase().includes(value.toLowerCase()) ||
                                        item.firstName.toLowerCase().includes(value.toLowerCase()) ||
                                        item.lastName.toLowerCase().includes(value.toLowerCase())||
                                        item?.notes?.toLowerCase().includes(value.toLowerCase()) ||
                                        item?.userName?.toLowerCase().includes(value.toLowerCase())

                                }
                            ).slice(0, 10);
                            const filteredPocs = res.pocs.filter(item => {
                                    return  item.ContactAddress.toString().toLowerCase().includes(value.toLowerCase()) ||
                                        item.ContactDescription.toLowerCase().includes(value.toLowerCase()) ||
                                        item.Notes.toLowerCase().includes(value.toLowerCase())
                                }
                            );
                            const filteredScripts = res.scripts.filter(item => {
                                    return  item.scriptName.toString().toLowerCase().includes(value.toLowerCase())
                                }
                            ).slice(0, 10);
                            const filteredDispositions = res.dispositions.filter(item => {
                                    return  item.dispositionName.toString().toLowerCase().includes(value.toLowerCase()) ||
                                item?.notes?.toLowerCase().includes(value.toLowerCase())
                                }
                            ).slice(0, 10);
                            this.resultSets = []
                            if (filteredAgents.length > 0)
                            {
                                this.resultSets.push({id:"agents", label:"Agents",results: filteredAgents})
                            }
                            if (filteredSkills.length > 0)
                            {
                                this.resultSets.push({id:"skills", label:"Skills",results: filteredSkills})
                            }
                            if (filteredPocs.length > 0)
                            {
                                this.resultSets.push({id:"pocs", label:"PointsOfContact",results: filteredPocs})
                            }
                            if (filteredScripts.length > 0)
                            {
                                this.resultSets.push({id:"scripts", label:"Scripts",results: filteredScripts})
                            }
                            if (filteredDispositions.length > 0)
                            {
                                this.resultSets.push({id:"dispositions", label:"Diospositions",results: filteredDispositions})
                            }

                            this.search.next(this.resultSets);
                        }
                    }
                )
            });
        //


    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On keydown of the search input
     *
     * @param event
     */
    onKeydown(event: KeyboardEvent): void
    {
        // Escape
        if ( event.code === 'Escape' )
        {
            // If the appearance is 'bar' and the mat-autocomplete is not open, close the search
            if ( this.appearance === 'bar' && !this._matAutocomplete.isOpen )
            {
                this.close();
            }
        }
    }

    /**
     * Open the search
     * Used in 'bar'
     */
    open(): void
    {
        // Return if it's already opened
        if ( this.opened )
        {
            return;
        }

        // Open the search
        this.opened = true;
    }

    /**
     * Close the search
     * * Used in 'bar'
     */
    close(): void
    {
        // Return if it's already closed
        if ( !this.opened )
        {
            return;
        }

        // Clear the search input
        this.searchControl.setValue('');

        // Close the search
        this.opened = false;
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    handleSearchSelect(object:string,result: any): string {
        switch (object){
            case 'skills':
            {
                return '/skills/' + result.skillId;
            }
            case 'agents':
            {
                return '/users/' + result.agentId;
            }
            case 'scripts':
            {
                return '/studio-scripts/' + result.scriptId;
            }
            case 'pocs':
            {
                //this._ICDataService.putPOCfilter(result.ContactCode)
                return '/points-of-contact';
            }
            case 'dispositions':
            {
                //this._ICDataService.putPOCfilter(result.ContactCode)
                return '/dispositions/'+ result.dispositionId;
            }

        }
        return '/home';
    }
}
